import { Image, ImageBackground } from 'expo-image';
import React from 'react';
import { StyleSheet, View } from 'react-native';

import {
  IconInfoFill,
  IconSodium,
  Text,
  XStack,
  YStack,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import { DeliveryFeesApply } from '@fhs-legacy/frontend/src/components/delivery-fees-apply';

import { useProductDisplayName } from './use-product-display-name';

type NonHeroProps = {
  displayName: string;
  description: string;
  variant: string;
  cals: string;
  imageUrl: string;
  showSodiumDisclaimer?: boolean;
  isStaticDetails: boolean;
  showUnavailabilityDisclaimer?: boolean;
};

export const NonHeroVariant = ({
  cals,
  imageUrl,
  displayName,
  variant,
  description,
  showSodiumDisclaimer,
  isStaticDetails,
  showUnavailabilityDisclaimer,
}: NonHeroProps) => {
  const mqStyles = usqMqStyles();
  const formattedDisplayName = useProductDisplayName({ displayName, isStaticDetails });

  return (
    <>
      <YStack style={styles.containerTitle}>
        <Text.Heading type="one" style={styles.textTitle}>
          {formattedDisplayName}
        </Text.Heading>
        <Text.Paragraph size="md" style={styles.textSubTitle}>
          {variant} • {cals}
          {showSodiumDisclaimer && (
            <>
              {' • '}
              <IconSodiumDisclaimer />
            </>
          )}
        </Text.Paragraph>
      </YStack>
      <ImageBackground
        contentFit="contain"
        source={require('../../assets/img/menu_item_detail_bg_2477_1058.webp')}
        style={styles.containerImageBackground}
      >
        <Image source={{ uri: imageUrl }} contentFit="contain" style={styles.image} />
      </ImageBackground>
      {showUnavailabilityDisclaimer && (
        <UnavailabilityDisclaimer isStaticDetails={isStaticDetails} />
      )}
      <Text.Paragraph size="md" style={[styles.textDescription, mqStyles.descriptionPadding]}>
        {description}
      </Text.Paragraph>
      <View style={[mqStyles.descriptionPadding]}>
        <DeliveryFeesApply textProps={{ weight: 'normal' }} containerProps={{ py: 2 }} />
      </View>
    </>
  );
};

function IconSodiumDisclaimer() {
  const iconSodiumSize = useMqSelect({ $ltDesktop: 16 }, 20);
  return <IconSodium size={iconSodiumSize} style={{ marginBottom: -4 }} />;
}

function UnavailabilityDisclaimer({ isStaticDetails }: { isStaticDetails?: boolean }) {
  const mqStyles = usqMqStyles();

  const text = `We're sorry, this product is currently ${
    isStaticDetails ? 'unavailable.' : 'unavailable at the selected store. '
  }`;

  return (
    <XStack style={mqStyles.unavailabilityDisclaimer}>
      <IconInfoFill size={20} />
      <Text.Paragraph size="md" style={styles.textSubTitle}>
        {text}
      </Text.Paragraph>
    </XStack>
  );
}

const styles = StyleSheet.create({
  containerTitle: {
    marginBottom: 8,
  },
  textTitle: {
    textAlign: 'center',
    lineHeight: 33.6,
  },
  textSubTitle: {
    textAlign: 'center',
  },
  containerImageBackground: {
    flex: 1,
    minHeight: 200,
  },
  image: {
    height: '100%',
    marginTop: -10,
  },
  textDescription: {
    minHeight: 92,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
});

const usqMqStyles = createMqStyles({
  descriptionPadding: {
    $base: {
      paddingHorizontal: 16,
    },
    $gteDesktop: {
      paddingHorizontal: 8,
    },
  },
  unavailabilityDisclaimer: {
    $base: {
      backgroundColor: tokens.colors.$houseLight,
      height: 44,
      justifyContent: 'center',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 8,
      marginHorizontal: 16,
    },
  },
});
